import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { BASE_URL } from "../../global";
import { toast } from "react-toastify";
import axiosWithToken from "../../utils/components/axiosTokenConfig";
import { encryptPassword } from "../master/userMaster/crypto";

export default function ResetPassword({ isMobile }) {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [userData, setUserData] = React.useState({
    username: "",
    password: "",
    confirmPassword: "",
  });
  const [passwordVisibility, setPasswordVisibility] = React.useState({
    password: false,
    confirmPassword: false,
  });
  // [lowercase, uppercase, number, minLength, maxLength]
  const [passwordRules, setPasswordRules] = React.useState([0, 0, 0, 0, 0]);
  const storage_userID = localStorage.getItem("id");

  const handleModalOpen = () => setIsModalOpen(true);
  const handleModalClose = () => setIsModalOpen(false);

  const handleInputChange = (field) => (event) => {
    setUserData({ ...userData, [field]: event.target.value });
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // ✅ Password validation logic
  const validatePassword = (value) => {
    let rules = [0, 0, 0, 0, 0];
    rules[0] = /[a-z]/.test(value) ? 1 : 0;
    rules[1] = /[A-Z]/.test(value) ? 1 : 0;
    rules[2] = /[0-9]/.test(value) ? 1 : 0;
    rules[3] = value.length >= 8 ? 1 : 0;
    rules[4] = value.length <= 32 ? 1 : 0;

    setPasswordRules(rules);
  };

  // Form validation
  const isPasswordValid = passwordRules.every((rule) => rule === 1);
  const isFormValid =
    userData.password &&
    userData.confirmPassword &&
    userData.password === userData.confirmPassword &&
    isPasswordValid;

  const handleSaveChanges = async () => {
    try {
      const encryptedPassword = await encryptPassword(userData.password);

      const response = await axiosWithToken.patch(
        BASE_URL + `users/${storage_userID}`,
        { password: encryptedPassword }
      );

      if (response.status === 200) {
        toast.success("Password updated successfully");
        handleModalClose();
        setUserData({
          ...userData,
          password: "",
          confirmPassword: "",
        });
        setPasswordRules([0, 0, 0, 0, 0]);
      }
    } catch (error) {
      console.error("Error updating password:", error);
    }
  };

  const passwordChangeModal = () => (
      <Modal open={isModalOpen} onClose={handleModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 550,
            "@media (max-width: 600px)": { width: "90%" },
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 3,
            maxHeight: "550px",
            overflow: "auto",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Edit Password
          </Typography>
          <TextField
            disabled
            value={userData.username || ""}
            fullWidth
            margin="normal"
          />
          <TextField
            label="New Password"
            type={passwordVisibility.password ? "text" : "password"}
            value={userData.password}
            onChange={(e) => {
              handleInputChange("password")(e);
              validatePassword(e.target.value);
            }}
            fullWidth
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility("password")}
                  >
                    {passwordVisibility.password ? (
                      <VisibilityIcon />
                    ) : (
                      <VisibilityOffIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Password Rules */}
          <Box mt={1} mb={2}>
            <Typography
              variant="body2"
              color={passwordRules[0] ? "success.main" : "error.main"}
            >
              • A lowercase letter
            </Typography>
            <Typography
              variant="body2"
              color={passwordRules[1] ? "success.main" : "error.main"}
            >
              • A capital (uppercase) letter
            </Typography>
            <Typography
              variant="body2"
              color={passwordRules[2] ? "success.main" : "error.main"}
            >
              • A number
            </Typography>
            <Typography
              variant="body2"
              color={passwordRules[3] ? "success.main" : "error.main"}
            >
              • Minimum 8 characters
            </Typography>
            <Typography
              variant="body2"
              color={passwordRules[4] ? "success.main" : "error.main"}
            >
              • Maximum 32 characters
            </Typography>
          </Box>
          <TextField
            label="Confirm Password"
            type={passwordVisibility.confirmPassword ? "text" : "password"}
            value={userData.confirmPassword}
            onChange={handleInputChange("confirmPassword")}
            fullWidth
            margin="normal"
          />
          {/* Password mismatch message */}
          {userData.confirmPassword &&
            userData.password !== userData.confirmPassword && (
              <Typography variant="body2" color="error.main">
                Passwords do not match
              </Typography>
            )}


          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            onClick={handleSaveChanges}
            disabled={!isFormValid}
          >
            Save Changes
          </Button>
        </Box>
      </Modal>
    );


  React.useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axiosWithToken.get(
          BASE_URL + `users/${storage_userID}`
        );

        if (response.status === 200) {
          const user = response.data;
          setUserData((prev) => ({
            ...prev,
            username: user[0].userName,
          }));
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  return (
    <>
      {passwordChangeModal()}
      {!isMobile ? (
        <Button
          onClick={handleModalOpen}
          sx={{
            justifyContent: "flex-start",
            gap: "12px",
            px: "16px",
            py: "9px",
            width: "100%",
            color: "#334155",
            textTransform: "uppercase",
            fontWeight: 700,
            fontSize: "0.8125rem",
            letterSpacing: "0.025em",
            borderBottom: "1px solid rgba(203,213,225,0.20)",
            "&:hover": { backgroundColor: "rgba(14,165,233,0.08)" },
          }}
        >
          <RestartAltIcon sx={{ color: "#1e40af", fontSize: "1.1rem" }} />
          RESET PASSWORD
        </Button>
      ) : (
        <ListItemButton
          onClick={handleModalOpen}
          sx={{
            py: '6.5px',
            pl: '12px !important',
            pr: '10px !important',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
          }}
        >
          <ListItemIcon sx={{ minWidth: "32px", color: '#475569', '& svg': { fontSize: '1.25rem' } }}>
            <Tooltip title="Reset Password" arrow placement="right">
              <RestartAltIcon />
            </Tooltip>
          </ListItemIcon>
          <Typography
            variant="p"
            component="div"
            align="left"
            style={{
              fontSize: '13.8px',
              fontWeight: 500,
              color: '#0f172a',
              fontFamily: '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              letterSpacing: '0.01em',
            }}
          >
            Reset Password
          </Typography>
        </ListItemButton>
      )}
    </>
  );
}